<template>
  <FormTemplatesFormCellContainer
    :form="form"
    :skeleton="skeleton"
    :disabled="disabled"
    :upable="upable"
    :downable="downable"
    :vertical="vertical"
    class="FormTemplatesFormCell"
    v-on="$listeners"
  >
    <v-row dense align="start">
      <v-col cols="12" sm="3" md="2">
        <BTextField
          v-model="form.name"
          :validate="{
            required: true,
            // 'regex': has.schedule ? [/^\d{2}\.\d{2}\.\d{4}$/, 'Название должно быть в формате ДД.ММ.ГГГГ'] : false,
          }"
          label="Название"
          :disabled="disabled"
          :skeleton="skeleton"
          :name="cellName"
        />
      </v-col>

      <v-col cols="12" sm="auto">
        <BTextField
          v-model="form.code"
          validate="required"
          label="Код"
          :disabled="disabled"
          :skeleton="skeleton"
          name="cell-code"
          style="max-width: 100px;"
        />
      </v-col>

      <v-col cols="12" sm="4" md="3" lg="3">
        <BAutocompleteCommon
          v-model="form.type"
          validate="required"
          $type="sheet-cell-type"
          :disabled="disabled"
          :skeleton="skeleton"
          name="cell-type"
          @change="onChangeType"
        />
      </v-col>

      <v-col cols="12" sm="auto">
        <BAutocompleteCommon
          v-model="form.type_method"
          :clearable="false"
          :type="form.type"
          $type="sheet-cell-type-method"
          :disabled="disabled"
          :skeleton="skeleton"
          name="cell-type-method"
          style="max-width: 180px;"
          @change="onChangeTypeMethod"
        />
      </v-col>

      <!--  ############################################################# -->

      <v-col v-if="has.auto_start" cols="12" sm="auto">
        <BTextField
          v-model.number="form.auto_start"
          validate="required|numeric|min_value:0"
          label="Начиная с"
          :disabled="disabled"
          :skeleton="skeleton"
          style="max-width: 120px;"
        />
      </v-col>

      <v-col v-if="has.list" cols="12">
        <FormTemplatesFormCombobox
          v-model="form.list"
          :skeleton="skeleton"
          validate="required"
          label="Список"
        />
      </v-col>

      <v-col v-if="has.list_handbook" cols="12" sm="4" md="3" lg="3">
        <BAutocompleteCommon
          validate="required"
          $type="forms-handbook"
          :disabled="disabled"
          :skeleton="skeleton"
          :value="form.list_handbook && form.list_handbook.id"
          clearable
          name="forms-handbook"
          @change="onChangeListHandbook"
        />
      </v-col>

      <template v-if="has.dep_list">
        <v-col cols="2">
          <BAutocompleteSelect
            v-model="form.dep_list_sheet_uuid"
            label="Страница"
            validate="required"
            no-data-text="Страница не найдена"
            :options="optionSheets"
            :return-object="false"
            item-text="name"
            item-value="uuid"
            @input="form.dep_list_cell_uuid = null"
          >
            <template #item="{ item }">
              <span>{{ item.name }}</span>
              <span class="caption text--secondary ml-2">
                ({{ item.code }})
              </span>
            </template>
          </BAutocompleteSelect>
        </v-col>

        <v-col cols="2">
          <BAutocompleteSelect
            v-model="form.dep_list_cell_uuid"
            validate="required"
            label="Ячейка"
            no-data-text="Ячейка не найдена"
            :options="getOptionCells(form.dep_list_sheet_uuid)"
            :return-object="false"
            :disabled="!form.dep_list_sheet_uuid"
            item-text="name"
            item-value="uuid"
            :autofocus="!form.dep_list_cell_uuid"
            :exclude="[form]"
            :multiple="!form.uuid"
            @input="setDepListValues"
          >
            <template #item="{ item }">
              <span>{{ item.name }}</span>
              <span class="caption text--secondary ml-2">
                ({{ item.code }})
              </span>
            </template>
          </BAutocompleteSelect>
        </v-col>
        <v-col v-if="form.dep_list_values" cols="12">
          <v-row
            v-for="(values, key) in form.dep_list_values"
            :key="key"
          >
            <v-col cols="2" class="text-right">
              <v-chip label class="mt-1">
                {{ key }}
              </v-chip>
            </v-col>
            <v-col cols="10">
              <FormTemplatesFormCombobox
                v-model="form.dep_list_values[key]"
                label="Список"
              />
            </v-col>
          </v-row>
        </v-col>
      </template>

      <v-col v-if="has.date_interval" cols="12" sm="6" md="5" lg="4">
        <FormTemplatesFormCellTypeDateInterval
          :value="[form.date_from, form.date_to]"
          :current-cell="form"
          :skeleton="skeleton"
          @input="form.date_from = $event[0]; form.date_to = $event[1]"
        />
      </v-col>

      <v-col v-if="has.number_interval" cols="12" sm="auto">
        <BTextField
          v-model.number="form.number_from"
          :validate="{
            required: true,
            numeric: true,
            min_value: 0,
            max_value: form.number_to || form.number_from,
          }"
          label="от"
          :disabled="disabled"
          :skeleton="skeleton"
          style="max-width: 120px;"
        />
      </v-col>

      <v-col v-if="has.number_interval" cols="12" sm="auto">
        <BTextField
          v-model.number="form.number_to"
          :validate="{
            required: true,
            numeric: true,
            min_value: form.number_from || form.number_to,
          }"
          label="до"
          :disabled="disabled"
          :skeleton="skeleton"
          style="max-width: 120px;"
        />
      </v-col>

      <v-col v-if="has.string_bound" cols="12" sm="auto">
        <BTextField
          v-model.number="form.string_min"
          :validate="{
            numeric: true,
            min_value: 1,
            max_value: form.string_max || form.string_min,
          }"
          label="min кол-во"
          :disabled="disabled"
          :skeleton="skeleton"
          style="max-width: 120px;"
        />
      </v-col>

      <v-col v-if="has.string_bound" cols="12" sm="auto">
        <BTextField
          v-model.number="form.string_max"
          :validate="{
            numeric: true,
            min_value: form.string_min || form.string_max,
          }"
          label="max кол-во"
          :disabled="disabled"
          :skeleton="skeleton"
          style="max-width: 120px;"
        />
      </v-col>

      <v-col v-if="has.formula" cols="12" sm="auto">
        <FormTemplatesFormCellTypeFormula
          v-model="form.formula"
          validate="required|formula"
          :sheet-tree="sheetTree"
          :current-sheet="currentSheet"
          :current-cell="form"
          :skeleton="skeleton"
          style="min-width: 400px;"
        />
      </v-col>

      <v-col v-if="has.list_cell" cols="12" sm="auto">
        <FormTemplatesFormCellTypeListCell
          v-model="form.list_cell"
          validate="required"
          :sheet-tree="sheetTree"
          :current-sheet="currentSheet"
          :current-cell="form"
        />
      </v-col>

      <!--  ############################################################# -->
    </v-row>
    <div
      slot="header"
      class="d-flex mx-lg-3 order-1 order-lg-0 align-center flex-wrap flex-lg-nowrap"
      style="gap: 10px;"
    >
      <v-checkbox
        v-model="form.required"
        :disabled="disabled"
        :skeleton="skeleton"
        dense
        hide-details
        name="cell-required"
        label="Поле обязательно?"
        class="ma-0 py-0"
        style="max-width: 200px;"
      />
      <v-checkbox
        v-model="form.unique"
        :disabled="disabled"
        :skeleton="skeleton"
        dense
        hide-details
        name="cell-required"
        label="Уникально?"
        class="ma-0 py-0"
        style="max-width: 200px;"
      />
      <v-checkbox
        v-if="!vertical"
        v-model="form.customColumnWidth"
        :disabled="disabled"
        :skeleton="skeleton"
        dense
        hide-details
        name="cell-auto-column-width"
        label="Заданная ширина столбца"
        class="ma-0 py-0"
        style="max-width: 250px;"
      />
      <v-checkbox
        v-if="has.string_bound"
        v-model="form.string_long"
        :disabled="disabled"
        :skeleton="skeleton"
        dense
        hide-details
        name="cell-string-long"
        label="Текст?"
        class="ma-0 py-0"
      />
      <div v-if="form.dependence.type" class="v-label theme--light">
        Зависит от {{ getCellDepsLabel(form.dependence) }}
      </div>
      <v-menu
        offset-y
        :close-on-content-click="false"
        min-width="600"
      >
        <template #activator="{ on, attrs }">
          <v-icon
            class="ml-1"
            v-bind="attrs"
            v-on="on"
          >
            mdi-pencil
          </v-icon>
        </template>
        <v-card v-if="form.dependence" class="pa-3">
          <v-row>
            <v-col>
              <v-checkbox
                v-model="form.required"
                :disabled="disabled"
                :skeleton="skeleton"
                hide-details
                label="Обязательное поле?"
                name="cell-required"
                class="ma-0"
              />
              <div class="mt-3 ml-1">
                Данное поле
                <template v-if="!form.required">
                  не
                </template>
                обязательно, если:
              </div>
            </v-col>
          </v-row>
          <v-row dense>
            <v-col cols="3">
              <BAutocompleteSelect
                v-model="form.dependence.type"
                label="Тип зависимости"
                :return-object="false"
                item-text="label"
                item-value="value"
                :options="sheetCellDepsTypeOptions"
              />
            </v-col>
          </v-row>
          <v-row v-if="form.dependence.type">
            <v-col>
              <v-row
                v-for="dependence in form.dependence.list"
                :key="dependence.cell_uuid"
                dense
              >
                <v-col cols="3">
                  <BAutocompleteSelect
                    v-model="dependence.sheet_uuid"
                    label="Страница"
                    validate="required"
                    no-data-text="Страница не найдена"
                    :options="optionSheets"
                    :return-object="false"
                    item-text="name"
                    item-value="uuid"
                    @input="dependence.cell_uuid = null"
                  >
                    <template #item="{ item }">
                      <span>{{ item.name }}</span>
                      <span class="caption text--secondary ml-2">
                        ({{ item.code }})
                      </span>
                    </template>
                  </BAutocompleteSelect>
                </v-col>

                <v-col cols="3">
                  <BAutocompleteSelect
                    v-model="dependence.cell_uuid"
                    validate="required"
                    label="Ячейка"
                    no-data-text="Ячейка не найдена"
                    :options="getOptionCells(dependence.sheet_uuid)"
                    :return-object="false"
                    :disabled="!dependence.sheet_uuid"
                    item-text="name"
                    item-value="uuid"
                    :autofocus="!dependence.cell_uuid"
                    :exclude="[form]"
                    :multiple="!form.uuid"
                  >
                    <template #item="{ item }">
                      <span>{{ item.name }}</span>
                      <span class="caption text--secondary ml-2">
                        ({{ item.code }})
                      </span>
                    </template>
                  </BAutocompleteSelect>
                </v-col>
                <v-col>
                  <FormTemplatesFormCombobox
                    v-model="dependence.list"
                    validate="required"
                    label="Имеет одно из значений:"
                    :predefined-items="getCellListByUuid(dependence.cell_uuid)"
                  />
                </v-col>
                <v-col cols="auto">
                  <v-btn
                    icon
                    @click="deleteDependence(dependence.cell_uuid)"
                  >
                    <v-icon>
                      mdi-delete
                    </v-icon>
                  </v-btn>
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <v-btn
                    outlined
                    @click="resetDependence"
                    v-text="'Убрать все зависимости'"
                  />
                </v-col>
                <v-spacer />
                <v-col>
                  <v-btn
                    depressed
                    @click="createDependence"
                    v-text="'Добавить зависимость'"
                  />
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-card>
      </v-menu>
    </div>
  </FormTemplatesFormCellContainer>
</template>

<script>
import omit from 'lodash/omit'
import uniqueId from 'lodash/uniqueId'
import {
  SheetCellDepsTypeOptions,
  SheetCellTypeMethods,
  SheetCellTypes,
} from '@/typings/api/FormTemplate'

import BTextField from '@/components/forms/BTextField.vue'
import BAutocompleteCommon from '@/components/forms/BAutocompleteCommon.vue'
import BAutocompleteSelect
  from '@/components/forms/BAutocompleteCommon/BAutocompleteSelect.vue'
import FormTemplatesFormCellContainer
  from './FormTemplatesFormCell/FormTemplatesFormCellContainer.vue'
import FormTemplatesFormCellTypeFormula
  from './FormTemplatesFormCell/FormTemplatesFormCellTypeFormula.vue'
import FormTemplatesFormCombobox
  from './FormTemplatesForm/FormTemplatesFormCombobox.vue'
import FormTemplatesFormCellTypeDateInterval
  from './FormTemplatesFormCell/FormTemplatesFormCellTypeDateInterval.vue'
import FormTemplatesFormCellTypeListCell
  from './FormTemplatesFormCell/FormTemplatesFormCellTypeListCell.vue'

export const createDefaultDependenceValueData = () => ({
  cell_uuid: null,
  sheet_uuid: null,
  list: [],
})

export const createDefaultDependenceData = () => ({
  type: null,
  list: [],
})

export const createDefaultData = (code, uuid) => ({
  name: `Ячейка ${code}`,
  uuid,
  description: '',
  hint: '',
  code: `C$${code}`,
  type: SheetCellTypes.number,
  type_method: SheetCellTypeMethods.manual,
  required: true,
  unique: false,
  customColumnWidth: false,
  dependence: createDefaultDependenceData(),

  list: [],

  auto_start: 1,

  number_from: null,
  number_to: null,

  string_min: null,
  string_max: null,
  string_long: false,

  date_from: null,
  date_to: null,

  formula: null,

  list_cell: null,

  list_handbook: null,

  dep_list_sheet_uuid: null,
  dep_list_cell_uuid: null,
  dep_list_values: null,
})

export default {
  name: 'FormTemplatesFormCell',
  inject: [
    'sheetTree',
  ],
  components: {
    BTextField,
    BAutocompleteCommon,
    BAutocompleteSelect,
    FormTemplatesFormCombobox,
    FormTemplatesFormCellContainer,
    FormTemplatesFormCellTypeFormula,
    FormTemplatesFormCellTypeDateInterval,
    FormTemplatesFormCellTypeListCell,
  },
  props: {
    skeleton: Boolean,
    disabled: Boolean,
    upable: Boolean,
    downable: Boolean,
    vertical: Boolean,
    form: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    sheetCellDepsTypeOptions: Object.values(SheetCellDepsTypeOptions),
  }),
  computed: {
    has() {
      const {
        type, type_method, customColumnWidth, string_long,
      } = this.form

      return {
        auto_start: (
          type_method === SheetCellTypeMethods.auto_start
        ),

        list: (
          type === SheetCellTypes.list_interval
          || type === SheetCellTypes.drop_list
        ),

        dep_list: (
          type_method === SheetCellTypeMethods.dep_list
        ),

        list_handbook: (
          type === SheetCellTypes.list_handbook
        ),

        date_interval: (
          type === SheetCellTypes.date_interval
          || type_method === SheetCellTypeMethods.date_interval
        ),

        number_interval: (
          type === SheetCellTypes.number_interval
          || type_method === SheetCellTypeMethods.number_interval
        ),

        string_bound: (
          type === SheetCellTypes.string
          && type_method === SheetCellTypeMethods.manual
        ),

        formula: (
          type === SheetCellTypes.formula
        ),

        list_cell: (
          type === SheetCellTypes.list_cell
        ),

        string_long: (
          string_long && type === SheetCellTypes.string
        ),

        schedule: type === SheetCellTypes.schedule,

        custom_width: customColumnWidth,
      }
    },
    currentSheet() {
      const { uuid } = this.form

      const cell = this.sheetTree.$all_cells
        .find((_) => _.uuid === uuid)

      return cell.$parent
    },
    optionSheets() {
      return this.sheetTree.$children || []
    },
    cellName() {
      return uniqueId('cell-name-')
    },
  },
  methods: {
    onChangeType() {
      this.form.type_method = createDefaultData().type_method
      this.onChangeTypeMethod()
    },
    onChangeListHandbook(v) {
      const { id } = v || {}
      this.form.list_handbook = id ? { id } : null
    },
    onChangeTypeMethod() {
      const data = omit(
        createDefaultData(),
        [
          'name',
          'uuid',
          'description',
          'hint',
          'code',
          'type',
          'type_method',
          'required',
          'dependence',
        ],
      )

      Object.entries(data).forEach(([key, value]) => {
        this.form[key] = value
      })
    },
    createDependence() {
      this.form.dependence.list.push(createDefaultDependenceValueData())
    },
    deleteDependence(cell_uuid) {
      this.form.dependence.list = this.form.dependence.list.filter((dependence) => dependence.cell_uuid !== cell_uuid)
    },
    resetDependence() {
      this.form.dependence = createDefaultDependenceData()
    },
    setDepListValues() {
      const values = {}
      this.getCellListByUuid(this.form.dep_list_cell_uuid).forEach((key) => {
        values[key] = []
      })
      this.form.dep_list_values = values
    },
    getCellDepsLabel(cellDependence) {
      return cellDependence.list.filter((dependence) => !!dependence.cell_uuid).map((dependence) => this.getCellByUuid(dependence.cell_uuid).name).join(', ')
    },
    getSelectedSheet(sheet_uuid) {
      return sheet_uuid && this.sheetTree.$children
        .find((sheet) => sheet.uuid === sheet_uuid)
    },
    getOptionCells(sheet_uuid) {
      return this.getSelectedSheet(sheet_uuid)?.$children || []
    },
    getCellByUuid(cell_uuid) {
      return this.sheetTree.$all_cells.find((cell) => cell.uuid === cell_uuid)
    },
    getCellListByUuid(cell_uuid) {
      const cell = this.getCellByUuid(cell_uuid)
      if (!cell) return []
      const values_list = [...cell.$data.list]
      Object.values(cell.$data.dep_list_values || {}).forEach((dep_values_list) => {
        dep_values_list.forEach((value) => {
          if (!(value in values_list)) {
            values_list.push(value)
          }
        })
      })
      return values_list
    },
  },
}
</script>

<style lang="scss">
.FormTemplatesFormCell {
  .v-input--selection-controls__input {
    margin: 0 !important;
  }
}
</style>
